import styled from "@emotion/styled";
import { motion, useAnimation } from "framer-motion";
import { arrayOf, bool, shape } from "prop-types";
import React, { useEffect, useState } from "react";

import { WERKENBIJ_PATH } from "../../constants";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import useWindowSize from "../../hooks/useWindowSize";
import theme from "../../theme";
import { customMq } from "../../utils/mediaQuery";
import Heading from "../Heading";
import Icon from "../Icon";
import Link from "../Link";
import { caption } from "../Typography";
import Person, { PersonType } from "./components/Person";

const Container = styled(motion.div)`
  display: grid;
  justify-content: center;
`;

const Item = styled(motion.div)`
  position: relative;
`;

const CtaIcon = styled(Icon)`
  width: 31px;
  height: 32px;
  transition: transform 300ms cubic-bezier(0.25, 0.9, 0.25, 1);
`;

const CtaCard = styled(motion.div)`
  position: relative;
  padding: ${theme.spacing("xs")};
  background-color: ${theme.color("background.dark")};
  transition: ${theme.transition()};
  a {
    color: #fff;
    &:hover {
      color: ${theme.color("secondary")};
      .icon {
        transform: translateX(5px);
      }
    }
  }
  ${customMq(400)} {
    padding: ${theme.spacing("s")};
  }
`;

const CtaHeading = styled(Heading)`
  font-size: ${theme.fs("bodySmall")};
  line-height: 1.6rem;
  ${customMq(400)} {
    line-height: 2rem;
  }
`;

const CtaBody = styled.div`
  ${caption};
  margin: ${theme.spacing("xxs")} 0;
  color: #fff;
  ${customMq(400)} {
    margin: ${theme.spacing("xs")} 0;
  }
`;

const Colleagues = ({ people, reveal }) => {
  const controls = useAnimation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeSibling, setActiveSibling] = useState(undefined);
  const [activeGrid, setActiveGrid] = useState(false);
  const size = useWindowSize();

  useEffect(() => {
    reveal && controls.start("end");
  }, [controls, reveal]);

  const listAnimation = {
    start: theme.get("animation.staggeredQuickReveal"),
    end: theme.get("animation.staggeredQuickReveal"),
  };

  const itemAnimation = {
    start: {
      opacity: 0,
      y: 40,
    },
    end: {
      opacity: 1,
      y: 0,
      transition: theme.get("animation.staggeredReveal.transition"),
      transitionEnd: { y: "unset" },
    },
  };

  const hoverEnd = () => {
    setActiveGrid(false);
    setActiveSibling(undefined);
  };

  const hoverStart = () => {
    setActiveGrid(true);
  };

  const tapAction = (siblingId, event) => {
    if (event.clientY >= 5 || event.clientY <= -5) setActiveSibling(siblingId);
  };

  const spring = { type: "spring", damping: 90, stiffness: 300 };
  const windowToGrid = Math.floor(size.width / 220);
  const gridSize = windowToGrid >= 2 ? windowToGrid : 2;

  return (
    <>
      <Container
        initial={reveal === undefined || prefersReducedMotion ? "" : "start"}
        animate={controls}
        variants={listAnimation}
        style={{
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
        }}
        onHoverEnd={hoverEnd}
      >
        {people.map((person, i) => (
          <Item
            key={person.id}
            variants={itemAnimation}
            layout
            positionTransition={spring}
            onHoverStart={hoverStart}
          >
            <Person
              data={person}
              index={i}
              activeSibling={activeSibling}
              onTapped={(event) => {
                tapAction(activeSibling === i ? null : i, event);
              }}
              flipSlide={gridSize}
              activeGrid={activeGrid}
            />
          </Item>
        ))}
        <CtaCard
          key={people.length}
          variants={itemAnimation}
          layout
          positionTransition={spring}
        >
          <Link href={WERKENBIJ_PATH}>
            <CtaHeading size={3}>WIL JIJ HIER OOK tussen STAAN?</CtaHeading>
            <CtaBody>Bekijk onze vacatures</CtaBody>
            <CtaIcon type="arrow" />
          </Link>
        </CtaCard>
      </Container>
    </>
  );
};

Colleagues.propTypes = {
  people: arrayOf(shape(PersonType)),
  reveal: bool,
};

export default Colleagues;
